// src/components/layout/SearchSection.jsx
import React, { useState, useRef, useCallback } from 'react';
import { useSearch } from '../../contexts/SearchContext';
import { motion, AnimatePresence } from 'framer-motion';
import AdvancedSearchForm from '../ui/AdvancedSearchForm';
import { SlidersIcon, SearchIcon } from '../common/IconsComponent';

const SearchSection = ({ onAfterSearch }) => {
  const { searchTerm, setSearchTerm, filters, setFilters, handleSearch } = useSearch();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const hasText = searchTerm.trim().length > 0;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      // Close the advanced-search panel (if it happened to be open) before
      // scrolling to results, so the panel's collapse animation doesn't
      // shift the layout after we've already scrolled.
      setShowAdvanced(false);
      handleSearch('quick');
      onAfterSearch?.();
    },
    [handleSearch, onAfterSearch]
  );

  const toggleAdvanced = () => setShowAdvanced((v) => !v);

  const clearSearch = () => {
    setSearchTerm('');
    inputRef.current?.focus();
  };

  // Advanced form submission: run the search, close the panel, then let
  // the caller (SearchPage) scroll to the results section.
  const handleAdvancedSearch = useCallback(() => {
    setShowAdvanced(false);
    handleSearch('advanced');
  }, [handleSearch]);

  return (
    <div dir="rtl">
      <form onSubmit={handleSubmit} autoComplete="off" role="search">
        <div className="flex items-center gap-2.5">
          <div
            className={`relative flex-1 min-w-0 flex items-center bg-white rounded-2xl border-2 transition-all duration-200 shadow-md ${
              isFocused
                ? 'border-white/80 shadow-[0_0_0_4px_rgba(255,255,255,0.18),0_4px_20px_rgba(0,0,0,0.15)]'
                : 'border-white/25 shadow-[0_4px_20px_rgba(0,0,0,0.13)] hover:shadow-[0_6px_26px_rgba(0,0,0,0.17)]'
            }`}
          >
            {/* Search icon */}
            <div
              className={`flex-shrink-0 flex items-center pr-3 pl-1 pointer-events-none transition-colors duration-200 ${
                isFocused ? 'text-indigo-500' : 'text-indigo-300'
              }`}
              aria-hidden="true"
            >
              <SearchIcon className="w-4 h-4" />
            </div>

            <input
              ref={inputRef}
              type="search"
              dir="auto"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="חפש קורסים, מקצועות או מורים..."
              className="flex-1 min-w-0 h-[50px] pr-1 text-base sm:text-[0.97rem] text-indigo-950 bg-transparent border-none outline-none placeholder-slate-300 text-right [direction:auto] [unicode-bidi:plaintext] [&::-webkit-search-cancel-button]:hidden pl-12 sm:pl-2.5"
              aria-label="חיפוש קורסים, מקצועות או מורים"
            />

            {/* Desktop clear button */}
            {hasText && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="נקה חיפוש"
                className="hidden sm:flex flex-shrink-0 items-center justify-center w-6 h-6 ml-2 rounded-full bg-slate-100 text-slate-400 hover:bg-indigo-100 hover:text-indigo-500 transition-colors duration-150"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="mx-auto">
                  <path
                    d="M1.5 1.5l9 9M10.5 1.5l-9 9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}

            {/* Mobile: show filters only when input is empty */}
            {!hasText && (
              <button
                type="button"
                onClick={toggleAdvanced}
                aria-expanded={showAdvanced}
                aria-controls="advanced-search-panel"
                aria-label="סינון מתקדם"
                className={`absolute left-2 top-1/2 -translate-y-1/2 sm:hidden flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 ${
                  showAdvanced
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-slate-100 text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                <SlidersIcon className="w-4 h-4 flex-shrink-0" />
              </button>
            )}

            {/* Mobile: show clear button only when there is text */}
            {hasText && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="נקה חיפוש"
                className="absolute left-2 top-1/2 -translate-y-1/2 sm:hidden flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 text-slate-400 hover:bg-indigo-100 hover:text-indigo-500 transition-colors duration-150"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="mx-auto">
                  <path
                    d="M1.5 1.5l9 9M10.5 1.5l-9 9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Desktop filters button */}
          <button
            type="button"
            onClick={toggleAdvanced}
            aria-expanded={showAdvanced}
            aria-controls="advanced-search-panel"
            className={`hidden sm:flex flex-shrink-0 items-center gap-1.5 h-[50px] px-4 rounded-xl border font-semibold text-sm transition-all duration-200 whitespace-nowrap ${
              showAdvanced
                ? 'bg-indigo-100 border-indigo-100 text-indigo-800'
                : 'bg-white border-white text-indigo-600 hover:bg-indigo-50 hover:border-indigo-50'
            }`}
          >
            <SlidersIcon className="w-4 h-4 flex-shrink-0" />
          </button>
        </div>
      </form>

      <AnimatePresence>
        {showAdvanced && (
          <motion.div
            id="advanced-search-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pt-2 border-t border-white/20">
              <AdvancedSearchForm
                filters={filters}
                setFilters={setFilters}
                handleSearch={handleAdvancedSearch}
                onAfterSearch={onAfterSearch}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchSection;